import logger from '@/app/logger';

export type ChargebeeGrowthUserContext = {
  customerId?: string;
  subscriptionId?: string;
  planName?: string;
  externalUserId?: string;
  email?: string;
  fullName?: string;
  roles?: string[];
};

type CancelPageUrls = {
  saveReturnUrl: string;
  cancelConfirmationUrl: string;
};

function splitName(fullName?: string) {
  if (!fullName) {
    return { firstName: undefined, lastName: undefined };
  }
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) {
    return { firstName: undefined, lastName: undefined };
  }
  return {
    firstName: parts[0],
    lastName: parts.length > 1 ? parts.slice(1).join(' ') : undefined
  };
}

function getChargebeeInstance() {
  return (window as any).Chargebee?.getInstance?.();
}

function buildUserPayload(context: ChargebeeGrowthUserContext) {
  const { firstName, lastName } = splitName(context.fullName);
  const roles = context.roles?.filter(Boolean);

  return {
    account: {
      customerId: context.customerId
    },
    externalUserId: context.externalUserId,
    email: context.email,
    firstName,
    lastName,
    roles,
    custom: roles?.length ? { roles } : undefined
  };
}

export async function initChargebeePersonalizedOffers(
  context: ChargebeeGrowthUserContext
) {
  if (!context.customerId) {
    return;
  }

  const chargebee = getChargebeeInstance();
  if (!chargebee?.personalizedOffers) {
    return;
  }

  try {
    const personalizedOffers = await chargebee.personalizedOffers();
    const initPayload: Record<string, any> = buildUserPayload(context);

    if (context.subscriptionId) {
      initPayload.subscription = { id: context.subscriptionId };
    }

    personalizedOffers.init(initPayload);
  } catch (error) {
    logger.error(
      `Chargebee Personalized Offers failed to initialize: ${
        (error && (error as any).message) || error
      }`
    );
  }
}

/**
 * Intercepts Cancel Subscription via Chargebee Cancel Page.
 * Returns true when the user was redirected to a retention/cancel experience.
 * Returns false when the caller should continue with the normal cancel flow.
 */
export async function openChargebeeCancelPage(
  context: ChargebeeGrowthUserContext,
  urls: CancelPageUrls
): Promise<boolean> {
  if (!context.subscriptionId) {
    return false;
  }

  const chargebee = getChargebeeInstance();
  if (!chargebee?.cancelPage) {
    return false;
  }

  try {
    const cancelPage = await chargebee.cancelPage();
    const userPayload = buildUserPayload(context);
    const options: Record<string, any> = {
      ...userPayload,
      subscription: {
        id: context.subscriptionId,
        ...(context.planName ? { plan: context.planName } : {})
      },
      saveReturnUrl: urls.saveReturnUrl,
      cancelConfirmationUrl: urls.cancelConfirmationUrl
    };

    const result = await cancelPage.getPage(options);
    if (result?.valid && result.url) {
      window.location.assign(result.url);
      return true;
    }

    if (result?.errorMessage) {
      logger.info(
        `Chargebee Cancel Page unavailable: ${result.errorMessage}`
      );
    }
  } catch (error) {
    logger.error(
      `Chargebee Cancel Page failed: ${
        (error && (error as any).message) || error
      }`
    );
  }

  return false;
}
